import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  Email as EmailIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { formatCurrency } from '../../utils/helper';

const SendInvoiceDialog = ({
  open,
  onClose,
  onConfirm,
  invoice,
  loading = false
}) => {
  const [error, setError] = useState('');

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleConfirm = () => {
    if (!invoice) {
      setError('No invoice selected');
      return;
    }

    onConfirm(invoice.id);
  };

  return (
    <Dialog
      open={open && invoice !== null}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        borderBottom: '1px solid #e0e0e0',
        pb: 2
      }}>
        <EmailIcon sx={{ fontSize: 28, color: 'primary.main' }} />
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Send Invoice via Email
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Invoice #{invoice?.invoice_number || 'N/A'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Invoice Info */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Invoice Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Customer: {invoice?.account_name || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: {invoice?.owner_email || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Amount: {formatCurrency(invoice?.taxable_invoice_value || 0)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Status: <Chip 
              label={invoice?.status_verbose || 'Unknown'} 
              size="small" 
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Typography>
        </Box>

        {/* Info Alert */}
        <Alert 
          severity="info" 
          icon={<SendIcon />}
          sx={{ mb: 3 }}
        >
          This will send the invoice PDF to the customer's email address. The invoice will be marked as shared.
        </Alert>

        {/* Warning for non-unpaid invoices */}
        {invoice?.status !== 'UP' && (
          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ mb: 3 }}
          >
            Only unpaid invoices can be sent via email. This invoice is currently {invoice?.status_verbose}.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || invoice?.status !== 'UP'}
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {loading ? 'Sending...' : 'Send Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendInvoiceDialog;
